import React, { useState, useEffect } from 'react';
import MenuItem from './MenuItem';
import { useMenuItems } from './useMenuItems';
import '../css/SideBar.css';
import LanguageSelector from './LanguageSelector';
import images from '../../../../constants/images';
import { FaSignOutAlt } from 'react-icons/fa';

const Sidebar = ({ activeView, onMenuSelect, onLogout, isMobileOpen, onCloseMobile }) => {
  // ---- Sidebar state ----
  const [collapsed, setCollapsed] = useState(false);
  const [activeMenu, setActiveMenu] = useState(activeView || 'bookings');
  const [hoverExpand, setHoverExpand] = useState(false);

  const MENU_ITEMS = useMenuItems();
  // ---- Handlers ----
  const toggleSidebar = () => setCollapsed(prev => !prev);

  const selectMenu = (menuId) => {
    setActiveMenu(menuId);
    if (onMenuSelect) {
      onMenuSelect(menuId);
    }
    // Close mobile menu after selection
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const onMouseEnterSidebar = () => {
    // Only enable hover expand on desktop
    if (window.innerWidth > 768) {
      setHoverExpand(true);
    }
  };
  
  const onMouseLeaveSidebar = () => {
    if (window.innerWidth > 768) {
      setHoverExpand(false);
    }
  };

  // Keep active menu synced with parent view
  useEffect(() => {
    if (activeView) {
      setActiveMenu(activeView);
    }
  }, [activeView]);

  // Handle mobile menu close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-toggle')) {
        if (onCloseMobile) {
          onCloseMobile();
        }
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when mobile menu is open
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileOpen, onCloseMobile]);

  // Reset collapsed state on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(false); // Always show full sidebar on mobile
        setHoverExpand(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className={`sidebar ${collapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
      onMouseEnter={onMouseEnterSidebar}
      onMouseLeave={onMouseLeaveSidebar}
    >
      {/* ---- HEADER ---- */}
      <div className="sidebar-header">
        <div className="logo">
          <img src={images.pho1986} alt="logo" className="logo-icon" />
        </div>

        {/* Only show collapse/expand buttons on desktop */}
        {window.innerWidth > 768 && (
          <>
            {collapsed && hoverExpand && (
              <button className="expand-btn" onClick={toggleSidebar} title="Expand sidebar">
                »
              </button>
            )}

            {!collapsed && (
              <button className="collapse-btn" onClick={toggleSidebar} title="Collapse sidebar">
                «
              </button>
            )}
          </>
        )}
      </div>

      {/* ---- MENU CONTENT ---- */}
      <div className="sidebar-content">
        {MENU_ITEMS.map((section, sectionIndex) => (
          <div key={sectionIndex} className="menu-section">
            {!collapsed && (
              <>
                <hr className="section-divider" />
                <div className="section-title">{section.section}</div>
              </>
            )}
            {section.items.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                isActive={activeMenu === item.id}
                isCollapsed={collapsed}
                onClick={() => selectMenu(item.id)}
              />
            ))}
          </div>
        ))}
        { !collapsed && <LanguageSelector/>}
      </div>

      {/* ---- LOGOUT BUTTON ---- */}
      <div className="sidebar-footer">
        <button
          className={`logout-btn ${collapsed ? 'collapsed' : ''}`}
          onClick={onLogout}
          title="Log out"
        >
          <FaSignOutAlt className="logout-icon" />
          {!collapsed && <span className="logout-text">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;